/**
 * ConsentRequestsPage – shows all pending and past consent requests (REQ-008)
 * Route: /traveler/consent-requests
 */
import { ConsentRequestModal } from "@/components/notifications/ConsentRequestModal";
import { useConsentRequests } from "@/hooks/useConsentRequests";
import { useI18nStore } from "@/stores/i18nStore";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export function ConsentRequestsPage() {
  const { t } = useI18nStore();
  const { pendingRequests, respond, responding } = useConsentRequests();
  const [searchParams] = useSearchParams();
  const handledRef = useRef<Set<string>>(new Set());

  // Handle actions from Service Worker notification click
  useEffect(() => {
    const action = searchParams.get("action");
    const id = searchParams.get("id");
    if (!action || !id || handledRef.current.has(id)) return;
    const req = pendingRequests.find((r) => r.id === id);
    if (!req) return;
    handledRef.current.add(id);
    if (action === "grant") respond(id, "GRANT");
    if (action === "deny") respond(id, "DENY");
  }, [searchParams, pendingRequests, respond]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {(t as unknown as Record<string, string>)["consentRequestsTitle"] ??
          "Access Requests"}
      </h1>

      {pendingRequests.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 opacity-40"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-lg font-medium">
            {(t as unknown as Record<string, string>)["consentRequestsEmpty"] ??
              "No pending requests"}
          </p>
          <p className="text-sm mt-1">
            {(t as unknown as Record<string, string>)[
              "consentRequestsEmptyHint"
            ] ??
              "You will be notified when someone requests access to your health data."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingRequests.map((req) => (
            <div
              key={req.id}
              className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 space-y-4"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">
                  {req.requesterRole === "MEDICAL" ? "🏥" : "✈️"}
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    {req.requesterName}
                  </p>
                  {req.requesterInstitution && (
                    <p className="text-sm text-gray-500">
                      {req.requesterInstitution}
                    </p>
                  )}
                  <p className="text-sm text-blue-700 font-medium mt-1">
                    {req.accessLevel === "EMERGENCY"
                      ? ((t as unknown as Record<string, string>)[
                          "consentAccessEmergency"
                        ] ?? "Emergency Data")
                      : req.accessLevel === "MEDICAL"
                        ? ((t as unknown as Record<string, string>)[
                            "consentAccessMedical"
                          ] ?? "Full Medical Data")
                        : ((t as unknown as Record<string, string>)[
                            "consentAccessOperator"
                          ] ?? "Travel Documents")}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => respond(req.id, "DENY")}
                  disabled={responding === req.id}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-medium
                             hover:border-red-300 hover:bg-red-50 hover:text-red-700
                             disabled:opacity-50 transition-all"
                >
                  ❌{" "}
                  {(t as unknown as Record<string, string>)["consentDeny"] ??
                    "Deny"}
                </button>
                <button
                  onClick={() => respond(req.id, "GRANT")}
                  disabled={responding === req.id}
                  className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-medium
                             hover:bg-green-700 disabled:opacity-50 transition-all shadow"
                >
                  ✅{" "}
                  {(t as unknown as Record<string, string>)["consentGrant"] ??
                    "Grant"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for first pending request if redirected here from push notification */}
      {pendingRequests.length > 0 && searchParams.get("modal") === "true" && (
        <ConsentRequestModal
          request={pendingRequests[0]!}
          onGrant={(id) => respond(id, "GRANT")}
          onDeny={(id) => respond(id, "DENY")}
          responding={responding === pendingRequests[0]!.id}
        />
      )}
    </div>
  );
}
